import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Star, Clock, CheckCircle, FileText, Award, Zap, Shield, Key, Wrench, DoorOpen, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { companyInfo, services, cities, testimonials, advantages } from '../../data/mock';

const iconMap = {
  glass: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </svg>
  ),
  key: Key,
  door: DoorOpen,
  clock: Clock,
  shield: Shield,
  wrench: Wrench,
  award: Award,
  zap: Zap,
  'file-text': FileText,
  'check-circle': CheckCircle
};

// Image de fond pour le Hero
const HERO_BG_IMAGE = "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=1920&q=80";

const HomePage = () => {
  return (
    <div className="w-full">
      {/* Hero Section avec image de fond */}
      <section className="relative text-white overflow-hidden min-h-[600px]">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${HERO_BG_IMAGE})`,
          }}
        />
        {/* Overlay sombre */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#262626]/95 via-[#262626]/85 to-[#262626]/70" />
        {/* Pattern subtil */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.02%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
        
        <div className="max-w-7xl mx-auto px-4 py-20 md:py-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#B91C1C] px-4 py-2 rounded-full mb-6">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-semibold">Vitry-sur-Seine • Val-de-Marne (94)</span>
              </div>
              
              {/* H1 SEO - VITRY-SUR-SEINE */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Vitrier, Serrurier à 
                <span className="text-[#B91C1C]"> Vitry-sur-Seine</span>
                <span className="block text-white mt-2">Dépannage 24h/24</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl">
                <strong>EMS Express</strong> : votre artisan vitrier et serrurier de confiance à 
                <strong> Vitry-sur-Seine</strong>. Vitrerie, miroiterie, serrurerie et menuiserie. 
                Intervention en <strong>30 minutes</strong>, devis gratuit.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                  <Button size="lg" className="w-full sm:w-auto bg-[#B91C1C] hover:bg-[#991B1B] text-white text-lg px-8 py-6">
                    <Phone className="w-5 h-5 mr-2" />
                    {companyInfo.phoneEmergency}
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white text-white hover:bg-white hover:text-[#262626] text-lg px-8 py-6">
                    Devis gratuit
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap gap-6 mt-8 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#B91C1C]" />
                  <span>Intervention 30 min</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#B91C1C]" />
                  <span>24h/24 - 7j/7</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#B91C1C]" />
                  <span>Devis gratuit</span>
                </div>
              </div>
            </div>
            
            {/* Logo EMS */}
            <div className="hidden lg:flex justify-center items-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#B91C1C]/20 rounded-3xl blur-2xl"></div>
                <img 
                  src={companyInfo.logo}
                  alt="EMS Express - Vitrier Serrurier Vitry-sur-Seine"
                  className="relative w-80 h-auto object-contain rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {advantages.map((adv) => {
              const IconComponent = iconMap[adv.icon] || CheckCircle;
              return (
                <div key={adv.id} className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#B91C1C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-[#B91C1C]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#262626] text-sm md:text-base">{adv.title}</p>
                    <p className="text-xs text-[#666666] hidden md:block">{adv.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#262626] mb-4">
              Nos Services à Vitry-sur-Seine
            </h2>
            <p className="text-lg text-[#666666] max-w-2xl mx-auto">
              Solutions complètes en vitrerie, serrurerie et menuiserie pour particuliers et professionnels
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || CheckCircle;
              const isUrgent = service.id === 'urgence';
              return (
                <Link key={service.id} to={`/services/${service.id}`}>
                  <Card className={`h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 ${
                    isUrgent ? 'border-[#B91C1C]/30 bg-[#B91C1C]/5' : 'border-transparent hover:border-[#B91C1C]/20'
                  }`}>
                    <CardContent className="p-6">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 bg-[#B91C1C]/10">
                        <IconComponent className="w-7 h-7 text-[#B91C1C]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#262626] mb-2">{service.title}</h3>
                      <p className="text-[#666666] mb-4">{service.shortDesc}</p>
                      {/* Sous-services preview */}
                      {service.subServices && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {service.subServices.slice(0, 3).map((sub) => (
                            <span key={sub.id} className="text-xs bg-gray-100 text-[#262626] px-2 py-1 rounded">
                              {sub.title}
                            </span>
                          ))}
                          {service.subServices.length > 3 && (
                            <span className="text-xs bg-[#B91C1C]/10 text-[#B91C1C] px-2 py-1 rounded">
                              +{service.subServices.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="mt-4 flex items-center font-medium text-[#B91C1C]">
                        En savoir plus
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nos Réalisations Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#262626] mb-4">
              Nos Réalisations à Vitry-sur-Seine
            </h2>
            <p className="text-lg text-[#666666] max-w-2xl mx-auto">
              Découvrez quelques-unes de nos réalisations en vitrerie et miroiterie dans le Val-de-Marne
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Réalisation 1 - Verrière de cuisine */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1e3bc990-adec-43d6-9ed9-a16fda0c2d3c/artifacts/9379kd3p_Capture%20d%27%C3%A9cran%202024-04-23%20151807.png"
                  alt="Verrière de cuisine style atelier - EMS Express Vitry-sur-Seine"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="text-xl font-bold mb-2">Verrière Style Atelier</h3>
                <p className="text-white/90 text-sm">
                  Installation d'une verrière intérieure sur mesure avec structure acier noir. 
                  Séparation cuisine/salon avec luminosité préservée.
                </p>
                <div className="flex items-center gap-2 mt-3 text-[#B91C1C]">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm font-medium">Vitry-sur-Seine (94)</span>
                </div>
              </div>
              <div className="absolute top-4 left-4">
                <span className="bg-[#B91C1C] text-white text-xs font-bold px-3 py-1 rounded-full">
                  Miroiterie
                </span>
              </div>
            </div>
            
            {/* Réalisation 2 - Paroi de douche */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1e3bc990-adec-43d6-9ed9-a16fda0c2d3c/artifacts/agestj9l_Capture%20d%27%C3%A9cran%202024-04-23%20152227.png"
                  alt="Paroi de douche en verre sur mesure - EMS Express Vitry-sur-Seine"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="text-xl font-bold mb-2">Paroi de Douche Premium</h3>
                <p className="text-white/90 text-sm">
                  Installation de parois de douche en verre trempé avec finition dorée. 
                  Design moderne et élégant pour salle de bain luxueuse.
                </p>
                <div className="flex items-center gap-2 mt-3 text-[#B91C1C]">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm font-medium">Val-de-Marne (94)</span>
                </div>
              </div>
              <div className="absolute top-4 left-4">
                <span className="bg-[#B91C1C] text-white text-xs font-bold px-3 py-1 rounded-full">
                  Vitrerie
                </span>
              </div>
            </div>
          </div>
          
          {/* CTA sous les réalisations */}
          <div className="text-center mt-10">
            <Link to="/contact">
              <Button className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-8 py-3">
                Demander un devis pour votre projet
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#262626] mb-4">
              Avis clients Vitry-sur-Seine
            </h2>
            <p className="text-lg text-[#666666]">
              La satisfaction client est notre priorité
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="h-full border-2 hover:border-[#B91C1C]/20 transition-colors">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#B91C1C] text-[#B91C1C]" />
                    ))}
                  </div>
                  <p className="text-[#262626] mb-4 italic">"{testimonial.text}"</p>
                  <div className="mt-auto">
                    <p className="font-semibold text-[#262626]">{testimonial.name}</p>
                    <p className="text-sm text-[#666666]">{testimonial.city} • {testimonial.service}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zones Section - VITRY EN PREMIER */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#262626] mb-4">
              Zones d'intervention autour de Vitry-sur-Seine
            </h2>
            <p className="text-lg text-[#666666]">
              Intervention rapide dans tout le Val-de-Marne (94)
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {cities.map((city) => (
              <Link
                key={city.id}
                to={`/zones/${city.id}`}
                className={`p-4 rounded-lg text-center transition-all duration-200 hover:shadow-md hover:-translate-y-1 ${
                  city.isPrimary 
                    ? 'bg-[#B91C1C] text-white hover:bg-[#991B1B]' 
                    : 'bg-white text-[#262626] hover:bg-[#B91C1C]/5 border border-gray-200'
                }`}
              >
                <p className="font-semibold text-sm">{city.name}</p>
                <p className={`text-xs mt-1 ${city.isPrimary ? 'text-white/70' : 'text-[#666666]'}`}>
                  {city.postalCode}
                </p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/zones">
              <Button variant="outline" className="border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white">
                Voir toutes les zones
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - ROUGE */}
      <section className="py-16 md:py-24 bg-[#B91C1C]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Besoin d'un artisan qualifié à Vitry-sur-Seine ?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Contactez EMS Express pour un devis gratuit ou une intervention rapide. 
              Disponible 24h/24 pour les urgences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                <Button size="lg" className="w-full sm:w-auto bg-white hover:bg-gray-100 text-[#B91C1C] text-lg px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  Appeler maintenant
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-[#B91C1C] text-lg px-8">
                  Demander un devis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;